import { latestOnly } from '../latest.ts';
import { SKELETON } from './components.ts';
import { explain } from './errors.ts';
import { esc, icon } from './format.ts';
import { revealView } from './nav.ts';
import { $ } from './ui.ts';

/**
 * Navigation and actions. Each view is a function that returns its screen's HTML; each view module
 * exports its table of actions: the buttons it answers (by data attribute: `data-lead-open` →
 * `leadOpen`), its forms (by id) and its controls (by id, or by data attribute for the rows of an
 * editor, which have no id). The console listens once on #app and hands each event to the table
 * that answers it.
 */

type View = () => Promise<string>;
/** A button's action: the button (busy while it runs) and its data attributes. */
export type ClickAction = (button: HTMLElement, data: DOMStringMap) => void;
export type Actions = {
  click?: Record<string, ClickAction>;
  submit?: Record<string, (ev: Event) => void>;
  change?: Record<string, (control: HTMLSelectElement) => void>;
  /** Controls without an id (a row of an editor), by data attribute: `data-manual-k` → `manualK`. */
  changeData?: Record<string, (control: HTMLSelectElement) => void>;
  /** A control as the person types in it (no request: a live preview), by data attribute. */
  inputData?: Record<string, (control: HTMLInputElement) => void>;
  /** The control the person focuses (where a chip inserts later), by data attribute. */
  focusData?: Record<string, (control: HTMLInputElement) => void>;
};

const views: Record<string, View> = {};
const tables: Actions[] = [];
/** After a screen is painted (the notifications badge follows what the person just did). */
let rendered = () => {};

export function registerViews(more: Record<string, View>) {
  Object.assign(views, more);
}
export function registerActions(...more: Actions[]) {
  tables.push(...more);
}
export function onRendered(run: () => void) {
  rendered = run;
}

/** The view on screen. */
export let current = 'resumen';
export function setCurrent(view: string) {
  current = view;
}

/** Only the latest render paints: a slow earlier one (the first Resumen) is dropped. */
const renders = latestOnly();

export async function render(view = current, { quiet = false } = {}) {
  current = view;
  // Only «Pólizas y recibos» has a URL of its own (#310): another screen leaves it.
  if (view !== 'polizas' && /[?&]vista=/.test(location.search))
    history.replaceState(null, '', location.pathname);
  const latest = renders.begin();
  // The menu, and on a phone its top bar too (Avisos and the main destination, #237).
  for (const b of document.querySelectorAll<HTMLButtonElement>('#nav button, #nav-quick button'))
    if (b.dataset.view === view) b.setAttribute('aria-current', 'page');
    else b.removeAttribute('aria-current');
  // The screen's group opens (a screen reached from another one, not from the menu, too).
  revealView(document, view);
  const app = $('#app');
  // The feedback widget's selector names what the person points at (dev/QA): «view-polizas».
  app.dataset.component = `view-${view}`;
  if (!quiet) app.innerHTML = SKELETON;
  app.setAttribute('aria-busy', 'true');
  try {
    const html = await (views[view] ?? views.resumen ?? (async () => ''))();
    if (!latest()) return;
    app.innerHTML = html;
    rendered();
  } catch (e) {
    if (!latest()) return;
    // Never leave the previous screen under a new tab: say what failed, offer to retry.
    const { human, detail, translated } = explain(e);
    const code = detail ? `<code>${esc(detail)}</code>` : '';
    app.innerHTML = `<div class="card empty error" data-component="load-error">${icon('alert')}<h3>No se pudo cargar esta pantalla</h3>
      <p>${esc(human)}</p>${translated ? `<details class="toast-detail"><summary>Detalles</summary>${code}</details>` : code}<button type="button" class="btn primary" data-retry>Reintentar</button></div>`;
  }
  app.removeAttribute('aria-busy');
}

/** Runs the action of a clicked button if a view's table answers it (its first data attribute that does). */
export function clickAction(button: HTMLElement): boolean {
  for (const t of tables)
    for (const [key, run] of Object.entries(t.click ?? {}))
      if (button.dataset[key] !== undefined) {
        run(button, button.dataset);
        return true;
      }
  return false;
}

/** Runs a form's submit if a view's table answers it (by the form's id). */
export function submitAction(ev: Event): boolean {
  const id = (ev.target as HTMLElement).id;
  for (const t of tables) {
    const run = id ? t.submit?.[id] : undefined;
    if (run) {
      run(ev);
      return true;
    }
  }
  return false;
}

/** Runs a control's change if a view's table answers it (by the control's id or data attribute). */
export function changeAction(ev: Event): boolean {
  const control = ev.target as HTMLSelectElement;
  for (const t of tables) {
    const run = control.id ? t.change?.[control.id] : undefined;
    if (run) {
      run(control);
      return true;
    }
  }
  for (const t of tables)
    for (const [key, run] of Object.entries(t.changeData ?? {}))
      if (control.dataset?.[key] !== undefined) {
        run(control);
        return true;
      }
  return false;
}

/** Hands an element's event to the first table that answers one of its data attributes. */
function byData(ev: Event, pick: (t: Actions) => Actions['inputData']): boolean {
  const control = ev.target as HTMLInputElement;
  for (const t of tables)
    for (const [key, run] of Object.entries(pick(t) ?? {}))
      if (control.dataset?.[key] !== undefined) {
        run(control);
        return true;
      }
  return false;
}

/** Runs what a view does as the person types in a control (by its data attribute). */
export function inputAction(ev: Event): boolean {
  return byData(ev, (t) => t.inputData);
}

/** Runs what a view does when the person focuses a control (by its data attribute). */
export function focusAction(ev: Event): boolean {
  return byData(ev, (t) => t.focusData);
}
